import { Theme } from '@mui/material/styles'

import { controlEdgeRing } from '../controlEdge'
import { layout } from '../tokens.generated'

// ----------------------------------------------------------------------
// A switch is an object with an edge, in both of its states.
//
// The off state used to be a grey fill and nothing else: `grey[500]` on a
// white card is 2.42:1, and on the fleet list, the vehicle record, App config
// and Rental terms that put every `Enable Freestyle` row's control below the
// threshold where it reads as a control at all. The word beside it was doing
// all the work — *Disabled* floating next to nothing.
//
// A fill may be quiet. A boundary may not, because the boundary is what says
// *this is a control*. So the off state is `surfaceSunken` ringed in
// `controlEdge`, the role that exists for exactly this: 3.41:1 against the
// track it rings, where `text3` — the first thing reached for — is a
// placeholder ink at 2.40:1 and fails the rule it was meant to satisfy.
//
// **The knob needs its own answer, and it is not the same answer twice.**
// Fixing the track alone looks fixed in a screenshot and is not: the knob is
// `common.white` and the new track is `#F4F5F7`, 1.09:1 — the part that
// actually carries the state was still invisible. So the off knob takes the
// same `controlEdge` ring.
//
// The on knob takes **no ring**, and that is the considered answer rather than
// an omission. White on the accent track is 4.84:1, so the knob's own fill is
// already the boundary. The obvious symmetry — ring it in the accent's dark
// slot — measures **1.02:1 against the accent in light mode**: a declaration
// that reads as the fix in the file, computes as nothing on the screen, and
// would have passed a test asserting only that a ring was declared. What the
// spec below asserts instead is that the knob is separable from its track, by
// whichever of its fill and its ring actually does the separating.
//
// **And the reason that answer was not enough is that the knob was not always
// on its track.** MUI's knob is 20px on a 14px track, so three pixels of it
// hang over the top, the bottom and the leading edge in every state, painted
// straight onto whatever is behind the switch. White on a white card is
// 1.00:1, which is why the checked switch on My plan read as a blue capsule
// with a bite taken out of it while every colour in this file measured
// correctly. The fix is `trackPadding` below: the track grows into the
// padding the knob was overhanging, so 4.84:1 against the accent is the number
// an operator actually sees. Nothing about the colours changed.
//
// `common.white` for the knob is deliberate and unchanged: the surface white
// inverts to near black in dark and would sit on a dark track with almost no
// separation.
//
// Every neutral rule is written `&:not(.Mui-checked)` rather than being left
// to source order. Two rules here are a compound class on `switchBase` and a
// sibling combinator, so `.Mui-checked + .track` and `.Mui-disabled + .track`
// have *identical* specificity and the later one silently wins — which is how
// the first draft of this file made a checked-and-disabled switch render
// pixel-identical to an off one. Excluding the state costs nine characters and
// makes the collision impossible; out-specifying it just moves the next
// collision one level up.
//
// Sizes are MUI's own; `size="small"` keeps its 40 x 24 box and 16px knob.
// ----------------------------------------------------------------------

// The ring, and the reason for its width, are in `theme/controlEdge`: the
// toggle pill draws the same one, and a shared value reached for through a
// component override is a value the next consumer re-types by hand.
export { controlEdgeRing }

/**
 * MUI's own box model, restated because the geometry below is derived from it
 * rather than guessed at. From `Switch.js` and `internal/SwitchBase.js` in
 * `@mui/material`: the root is `track + 2 * padding`, and the knob is centred
 * in the root by `SwitchBase`'s own padding.
 *
 * Restating them here means a MUI bump that changes the box fails the spec in
 * `offState.test.ts` rather than silently drawing a wrong track.
 */
export const SWITCH_BOX = {
  medium: { root: 38, width: 58, knob: 20, knobInset: 9, travel: 20 },
  small: { root: 24, width: 40, knob: 16, knobInset: 4, travel: 16 },
} as const

/**
 * The padding that makes the track enclose the knob.
 *
 * **This is the whole of the checked-knob fix, and it is geometry rather than
 * colour.** MUI draws a 20px knob on a 14px track, so 3px of the knob is
 * painted directly on whatever is behind the switch. On a white card that is
 * white on white — 1.00:1 — and the operator sees a blue capsule with an
 * invisible bite out of it rather than a knob at one end of a track. The off
 * knob never showed it because it keeps a `controlEdge` ring; the on knob has
 * none, and it cannot have one: `controlEdge` on the accent measures 1.30:1,
 * under the floor `offState.test.ts` holds every declared ring to.
 *
 * So the knob is put inside the track instead. The root's outer box does not
 * move, so no row changes height and nothing beside a switch shifts; only the
 * track grows into the padding the knob was overhanging.
 */
const trackPadding = (box: (typeof SWITCH_BOX)[keyof typeof SWITCH_BOX]) =>
  (box.root - box.knob) / 2 - layout.switchTrackInset

/**
 * How far inside its own box a switch actually paints, per size.
 *
 * `ControlLabel` needs this and cannot derive it: the gap between a control
 * and its word is measured from the edge the control *paints*, and for a
 * switch that edge is the track, which starts one `trackPadding` in. Exported
 * rather than restated so the two files cannot drift — the whole reason the
 * word sat 21px from a checkbox was three numbers in three files that nothing
 * added up.
 */
export const switchPaintInset = (size: keyof typeof SWITCH_BOX) =>
  trackPadding(SWITCH_BOX[size])

export default function Switch(theme: Theme) {
  const isLight = theme.palette.mode === 'light'
  const edge = controlEdgeRing(theme.palette.controlEdge)

  return {
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: trackPadding(SWITCH_BOX.medium),
        },
        sizeSmall: {
          padding: trackPadding(SWITCH_BOX.small),
        },
        track: {
          opacity: 1,
          backgroundColor: theme.palette.background.neutral,
          boxShadow: edge,
          // MUI hard-codes half of its own 14px track height. Once the track
          // is 24 tall that stops being a pill and starts being a rounded
          // rectangle with a knob rattling inside it.
          borderRadius: theme.radii.pill,
        },
        switchBase: {
          'left': 0,
          'right': 'auto',
          'color': theme.palette.common.white,

          // Off, in every combination of off. Scoped away from the checked
          // state rather than ordered before it.
          '&:not(.Mui-checked) .MuiSwitch-thumb': {
            boxShadow: edge,
          },

          // On. The knob's own fill is the boundary here, so the ring is
          // removed rather than restated in a colour that cannot be seen.
          '&.Mui-checked': {
            color: theme.palette.primary.contrastText,
          },
          '&.Mui-checked .MuiSwitch-thumb': {
            boxShadow: 'none',
          },
          '&.Mui-checked + .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: theme.palette.primary.main,
            boxShadow: controlEdgeRing(theme.palette.primary.main),
          },

          // A control that cannot be used is still an object, and it is still
          // in a state. Unavailable is said by a chip, a cursor and a reason —
          // never by erasing the edge, and never by greying the track, which
          // would make a withdrawn-but-enabled feature read as switched off.
          // So disabled keeps whichever track its state draws and only quiets
          // the knob. Both rules exclude the other state rather than relying on
          // being written after it.
          '&.Mui-disabled': {
            cursor: 'not-allowed',
          },
          '&.Mui-disabled:not(.Mui-checked)': {
            color: theme.palette.grey[isLight ? 400 : 600],
          },
          '&.Mui-disabled:not(.Mui-checked)+.MuiSwitch-track': {
            opacity: 1,
            backgroundColor: theme.palette.background.neutral,
            boxShadow: edge,
          },
          '&.Mui-disabled:not(.Mui-checked) .MuiSwitch-thumb': {
            boxShadow: edge,
          },
        },
      },
    },
  }
}
